import type {
  ChannelThreadId,
  MessageId,
  SupportTicketId,
} from "@redotech/effect-api-shared/ids/definitions";
import type { MessageChannel } from "@redotech/effect-api-shared/models/shopper-ai/conversation/message";
import { useRequiredContext } from "@redotech/react-util/context";
import { useTriggerLoad } from "@redotech/react-util/load";
import DotsVerticalIcon from "@redotech/redo-icons/dots-vertical.svg";
import EyeOffFilledIcon from "@redotech/redo-icons/eye-off_filled.svg";
import EyeIcon from "@redotech/redo-icons/eye.svg";
import HeartIcon from "@redotech/redo-icons/heart.svg";
import HeartFilledIcon from "@redotech/redo-icons/heart_filled.svg";
import TrashIcon from "@redotech/redo-icons/trash-01.svg";
import { EffectApiClientContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import { RedoSpinner } from "@redotech/redo-web/arbiter-components/loading/redo-spinner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@redotech/redo-web/components/ui/dropdown-menu";
import { Flex } from "@redotech/redo-web/flex";
import { memo, useState } from "react";
import { catchApiErrors } from "../../db/error-handling";
import { useGlobalSupportTicketCollections } from "../../providers/global-support-ticket-collections-context";

interface MetaCommentActionsProps {
  messageId: MessageId;
  threadId: ChannelThreadId;
  commentId: string;
  channel: MessageChannel;
  supportTicketId: SupportTicketId;
  hidden?: boolean;
  liked?: boolean;
}

export const MetaCommentActions = memo(function MetaCommentActions({
  messageId,
  threadId,
  commentId,
  channel,
  supportTicketId,
  hidden = false,
  liked = false,
}: MetaCommentActionsProps) {
  const effectApi = useRequiredContext(EffectApiClientContext);
  const collections = useGlobalSupportTicketCollections();
  const [open, setOpen] = useState(false);

  const platform: "facebook" | "instagram" =
    channel === "facebookComments" ? "facebook" : "instagram";
  const isFacebook = platform === "facebook";

  const [visibilityLoad, triggerVisibility] = useTriggerLoad(async () => {
    const client = effectApi.getClient();
    const call = client.supportTicket.toggleCommentVisibility({
      payload: { threadId, messageId, commentId, platform, hide: !hidden },
    });
    const { message } = await effectApi.runPromise(catchApiErrors(call));
    collections.messages.utils.writeUpdate({
      id: message.id,
      channelData: message.channelData,
    });
    setOpen(false);
  });

  const [deleteLoad, triggerDelete] = useTriggerLoad(async () => {
    if (!confirm("Are you sure you want to delete this comment?")) {
      return;
    }
    const client = effectApi.getClient();
    const call = client.supportTicket.deleteComment({
      payload: { threadId, messageId, commentId, platform },
    });
    const { message } = await effectApi.runPromise(catchApiErrors(call));
    collections.messages.utils.writeUpdate({
      id: message.id,
      channelData: message.channelData,
    });
    setOpen(false);
  });

  const [likeLoad, triggerLike] = useTriggerLoad(async () => {
    if (!isFacebook) return;
    const client = effectApi.getClient();
    const { message } = liked
      ? await effectApi.runPromise(
          catchApiErrors(
            client.supportTicket.unlikeComment({
              payload: { threadId, messageId, commentId },
            }),
          ),
        )
      : await effectApi.runPromise(
          catchApiErrors(
            client.supportTicket.likeComment({
              payload: { threadId, messageId, commentId },
            }),
          ),
        );
    collections.messages.utils.writeUpdate({
      id: message.id,
      channelData: message.channelData,
    });
    setOpen(false);
  });

  const loading =
    visibilityLoad.pending || deleteLoad.pending || likeLoad.pending;

  return (
    <DropdownMenu onOpenChange={setOpen} open={open}>
      <div className="flex items-center gap-1">
        {loading && (
          <Flex>
            <RedoSpinner size="xs" />
          </Flex>
        )}
        <DropdownMenuTrigger
          className="flex h-6 w-6 items-center justify-center rounded opacity-0 transition-opacity hover:bg-gray-100 group-hover:opacity-100"
          onClick={(e) => e.stopPropagation()}
        >
          <DotsVerticalIcon className="h-4 w-4" />
        </DropdownMenuTrigger>
      </div>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          disabled={loading}
          onClick={() => triggerVisibility()}
        >
          {hidden ? (
            <>
              <EyeIcon className="mr-2 h-4 w-4" />
              <span>Unhide comment</span>
            </>
          ) : (
            <>
              <EyeOffFilledIcon className="mr-2 h-4 w-4" />
              <span>Hide comment</span>
            </>
          )}
        </DropdownMenuItem>
        {isFacebook && (
          <DropdownMenuItem disabled={loading} onClick={() => triggerLike()}>
            {liked ? (
              <>
                <HeartIcon className="mr-2 h-4 w-4" />
                <span>Unlike comment</span>
              </>
            ) : (
              <>
                <HeartFilledIcon className="mr-2 h-4 w-4" />
                <span>Like comment</span>
              </>
            )}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          className="text-red-600"
          disabled={loading}
          onClick={() => triggerDelete()}
        >
          <TrashIcon className="mr-2 h-4 w-4" />
          <span>Delete comment</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
});
